import { useState, useEffect } from 'react';
import { Heart, ExternalLink, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getFavorites, removeFavorite, setPriceAlert, FavoriteShoe } from '../utils/api';
import toast from 'react-hot-toast';
import { usePriceAlert } from '../hooks/usePriceAlert';


const FavoritesPage = () => {
  const { isAuthenticated } = useAuth();
  const { priceAlertSettings, handleToggleGlobalAlert, handleThresholdChange } = usePriceAlert();
  const [favorites, setFavorites] = useState<FavoriteShoe[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPriceAlert, setShowPriceAlert] = useState<string | null>(null);
  const [alertPrice, setAlertPrice] = useState<string>('');

  // Load favorites from API (2025-10-10: Free for all registered users)
  useEffect(() => {
    const loadFavorites = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const response = await getFavorites();
        if (response.success && response.data) {
          setFavorites(response.data.favorites);
        } else {
          throw new Error(response.error || 'Failed to load favorites');
        }
      } catch (error: any) {
        console.error('Error loading favorites:', error);
        toast.error(error.message || 'Failed to load favorites');
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [isAuthenticated]);

  const handleRemoveFavorite = async (shoeId: string) => {
    if (!confirm('Remove this shoe from your favorites?')) {
      return;
    }

    try {
      const response = await removeFavorite(shoeId);
      if (response.success) {
        setFavorites(prev => prev.filter(fav => fav.shoeId !== shoeId));
        toast.success('Shoe removed from favorites');
      } else {
        throw new Error(response.error || 'Failed to remove favorite');
      }
    } catch (error: any) {
      console.error('Error removing favorite:', error);
      toast.error(error.message || 'Failed to remove favorite');
    }
  };

  const handleSetPriceAlert = async (shoeId: string) => {
    const price = parseFloat(alertPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      const response = await setPriceAlert(shoeId, price);
      if (response.success && response.data) {
        setFavorites(prev => prev.map(fav =>
          fav.shoeId === shoeId && response.data
            ? response.data.favorite
            : fav
        ));
        setShowPriceAlert(null);
        setAlertPrice('');
        toast.success(`✅ Price alert set! We'll notify you when the price drops to $${price}`);
      } else {
        throw new Error(response.error || 'Failed to set price alert');
      }
    } catch (error: any) {
      console.error('Error setting price alert:', error);
      toast.error(error.message || 'Failed to set price alert');
    }
  };

  const handleToggleShoeAlert = (shoeId: string) => {
    const shoe = favorites.find(fav => fav.shoeId === shoeId);
    if (!shoe) return;

    const currentAlert = shoe.priceAlert;
    const willActivateAlert = !currentAlert || !currentAlert.isActive;

    // If activating alert and global is disabled, enable global first
    if (willActivateAlert && !priceAlertSettings.isEnabled) {
      handleToggleGlobalAlert();
    } else if (willActivateAlert) {
      toast.success(
        `Now watching ${shoe.shoeName}! We'll email you when the price drops ${priceAlertSettings.threshold}% or more.`,
        { duration: 4000 }
      );
    } else {
      toast('Email alert disabled for ' + shoe.shoeName, { duration: 3000 });
    }

    // Toggle individual shoe alert
    setFavorites(prev => prev.map(fav => {
      if (fav.shoeId === shoeId) {
        if (currentAlert) {
          // Toggle existing alert
          return {
            ...fav,
            priceAlert: {
              ...currentAlert,
              isActive: !currentAlert.isActive
            }
          };
        } else {
          // Create new alert with current price as base and global threshold
          return {
            ...fav,
            priceAlert: {
              targetPrice: fav.currentPrice ? fav.currentPrice * (1 - priceAlertSettings.threshold / 100) : 0,
              isActive: true
            }
          };
        }
      }
      return fav;
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <Heart className="h-16 w-16 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Sign in to view your favorites
          </h2>
          <p className="text-gray-600 mb-6">
            Create an account to save your favorite shoes and get price alerts.
          </p>
          <button className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Favorite Shoes</h1>
          <p className="text-gray-600">
            Manage your saved shoes and set up price alerts to get notified of deals.
          </p>
        </div>

        {/* Price Alert Control Bar */}
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <Bell className="h-5 w-5 text-orange-600" />
              <span className="font-medium text-orange-800">Price Alert:</span>

              <select
                value={priceAlertSettings.threshold}
                onChange={(e) => handleThresholdChange(Number(e.target.value))}
                className="px-3 py-1.5 text-sm bg-white border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-orange-700"
              >
                <option value={5}>5%</option>
                <option value={10}>10%</option>
                <option value={15}>15%</option>
                <option value={20}>20%</option>
                <option value={30}>30%</option>
                <option value={40}>40%</option>
                <option value={50}>50%</option>
              </select>

              <button
                onClick={handleToggleGlobalAlert}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                  priceAlertSettings.isEnabled ? 'bg-orange-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    priceAlertSettings.isEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-orange-800">
                {priceAlertSettings.isEnabled ? 'ON' : 'OFF'}
              </span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            <span className="ml-3 text-gray-600">Loading your favorites...</span>
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Heart className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              No favorites yet
            </h2>
            <p className="text-gray-600 mb-6">
              Start adding shoes to your favorites from the recommendation page!
            </p>
            <button className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors">
              Get Recommendations
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((favorite) => (
              <div
                key={favorite._id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="relative">
                  <img
                    src={favorite.shoeImage}
                    alt={favorite.shoeName}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-3 right-3 flex space-x-2">
                    <button
                      onClick={() => handleToggleShoeAlert(favorite.shoeId)}
                      className={`p-2 rounded-full shadow-md transition-all duration-200 ${
                        favorite.priceAlert?.isActive
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'bg-white text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                      }`}
                      title={
                        favorite.priceAlert?.isActive
                          ? `✅ Alert active (${priceAlertSettings.threshold}% off)`
                          : `Click to enable price alert (${priceAlertSettings.threshold}% off)`
                      }
                    >
                      <Bell className={`h-4 w-4 ${favorite.priceAlert?.isActive ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleRemoveFavorite(favorite.shoeId)}
                      className="p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors group"
                      title="Remove from favorites"
                    >
                      <Heart className="h-4 w-4 text-red-500 fill-current group-hover:text-red-600" />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="font-bold text-gray-900 text-lg mb-1">
                      {favorite.shoeName}
                    </h3>
                    <p className="text-sm text-gray-500">{favorite.shoeBrand}</p>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between">
                      {favorite.currentPrice && (
                        <div className="text-2xl font-bold text-green-600">
                          ${favorite.currentPrice}
                        </div>
                      )}
                      {favorite.originalPrice && favorite.currentPrice && favorite.originalPrice > favorite.currentPrice && (
                        <div className="text-sm text-gray-500">
                          <span className="line-through">${favorite.originalPrice}</span>
                          <span className="text-red-600 font-semibold ml-1">
                            {Math.round(((favorite.originalPrice - favorite.currentPrice) / favorite.originalPrice) * 100)}% OFF
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => window.open(favorite.shoeUrl, '_blank')}
                      className="w-full flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Product
                    </button>

                    {!favorite.priceAlert && (
                      <button
                        onClick={() => setShowPriceAlert(favorite.shoeId)}
                        className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Set Price Alert
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Price Alert Modal */}
        {showPriceAlert && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center mb-4">
                <Bell className="h-5 w-5 text-orange-600 mr-2" />
                <h3 className="text-lg font-bold text-gray-900">Set Price Alert</h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                We'll notify you when the price drops to or below your target price.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Price ($)
                </label>
                <input
                  type="number"
                  value={alertPrice}
                  onChange={(e) => setAlertPrice(e.target.value)}
                  placeholder="Enter target price"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPriceAlert(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSetPriceAlert(showPriceAlert)}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
                >
                  Set Alert
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default FavoritesPage;